import { ArrowRight, Laptop, Camera, Fingerprint } from 'lucide-react';

const PRODUCTS_DATA = [
  {
    id: 'laptops',
    title: 'Laptops & Desktops',
    description: 'Branded new and refurbished Lenovo & HP laptops for business and personal use.',
    brands: ['Lenovo', 'HP'],
    image: 'https://images.pexels.com/photos/4065617/pexels-photo-4065617.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    icon: Laptop,
    span: 'md:col-span-2 md:row-span-2',
  },
  {
    id: 'cctv',
    title: 'CCTV Cameras',
    description: 'Complete surveillance solutions with CP Plus and Hikvision cameras.',
    brands: ['CP Plus', 'Hikvision'],
    image: 'https://images.pexels.com/photos/96612/pexels-photo-96612.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    icon: Camera,
    span: 'md:col-span-1 md:row-span-1',
  },
  {
    id: 'biometrics',
    title: 'Biometric Systems',
    description: 'Advanced fingerprint and access control systems for offices.',
    brands: ['eSSL', 'ZKTeco'],
    image: 'https://images.pexels.com/photos/11934176/pexels-photo-11934176.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    icon: Fingerprint,
    span: 'md:col-span-1 md:row-span-1',
  },
];

export const ProductsSection = () => {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="py-20 md:py-32 bg-slate-50"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mb-16 opacity-0 animate-fade-in-up">
          <span className="text-sm font-medium tracking-widest uppercase text-orange-500 mb-4 block">
            What We Offer
          </span>
          <h2
            data-testid="products-title"
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4"
          >
            Our Products
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl">
            Quality IT hardware from trusted brands. We are authorized dealers for leading manufacturers.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[280px]">
          {PRODUCTS_DATA.map((product, index) => {
            const IconComponent = product.icon;
            return (
              <div
                key={product.id}
                data-testid={`product-card-${product.id}`}
                className={`${product.span} group relative overflow-hidden rounded-sm bg-white border border-slate-200 card-hover opacity-0 animate-fade-in-up`}
                style={{ animationDelay: `${index * 100 + 200}ms` }}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover img-zoom opacity-90"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
                </div>

                {/* Content */}
                <div className="relative h-full p-6 md:p-8 flex flex-col justify-end z-10">
                  {/* Icon */}
                  <div className="w-12 h-12 bg-orange-500 rounded-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 uppercase tracking-tight">
                    {product.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-300 mb-4 text-sm md:text-base">
                    {product.description}
                  </p>

                  {/* Brands */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.brands.map((brand) => (
                      <span
                        key={brand}
                        className="bg-white/10 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full"
                      >
                        {brand}
                      </span>
                    ))}
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center gap-2 text-orange-400 font-medium text-sm uppercase tracking-wider group-hover:text-orange-300 transition-colors">
                    View Products
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
